import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import MealBasketPageListMeals from './meal_basket_page_list_meals';
import {
  DEFAULT_SHOPPING_LIST_SERVINGS,
  NEW_SHOPPING_LIST_PSEUDO_ID,
  newShoppingListWithDefaultFields,
} from '../services/shopping_lists';
import MealBasketPageNewList from './meal_basket_page_new_list';
import { userLocale } from '../../../services/users';
import {
  useCreateProductsBoardMutation,
  useCreateShoppingListMutation,
  useGetShoppingListsByProductsBoardQuery,
  useLazyListProductsBoardQuery,
  useListProductsBoardQuery,
  useUpdateProductsBoardMutation,
  useUpdateShoppingListMutation,
} from '../../../app/smorgAppSyncGraphQLApi';
import {
  mealBasketCleared,
  mealBasketSelectedShoppingListIDChanged,
  mealsRemovedFromMealBasket,
} from '../end_user_shopping_slice';
import { compileShoppingListOperation } from '../../../operations/meal_operations';
import { useTracker } from '../../../operations/tracking_operations';
import {
  productsBoardWithShoppingListGroupsReplaced,
  shoppingListGroupWithShoppingListAdded,
} from '../../../services/products_board';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../../app/smorgAppSyncGraphQLApiCustomHooks';
import MealBasketPageLoading from './meal_basket_page_loading';
import { ensureDefaultProductsBoardExists } from '../../../operations/shopping_lists_operations';

const MealBasketContainer = ({
  onClosePopover,
  setSnackbarNotificationText,
}) => {
  const dispatch = useDispatch();

  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  const locale = userLocale(userProfile);

  const { data: productsBoard, isLoading: isProductsBoardLoading } =
    useListProductsBoardQuery();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: existingShoppingLists, isLoading: shoppingListsLoading } =
    useGetShoppingListsByProductsBoardQuery(productsBoard?.id, {
      skip: !productsBoard?.id,
    });

  const [updateProductsBoard] = useUpdateProductsBoardMutation();

  const [createShoppingList] = useCreateShoppingListMutation();

  const [updateShoppingList] = useUpdateShoppingListMutation();

  const productsBoardID = productsBoard?.id;

  const mealBasketItems = useSelector(
    (state) => state.endUserShopping.mealBasketItems,
  );

  const selectedShoppingListID = useSelector(
    (state) => state.endUserShopping.selectedShoppingListID,
  );

  const mealRemovedFromMealBasket = (mealId) =>
    dispatch(mealsRemovedFromMealBasket([mealId]));

  const clearMealBasket = () => dispatch(mealBasketCleared());

  const selectedShoppingListIDChanged = (shoppingListId) =>
    dispatch(mealBasketSelectedShoppingListIDChanged(shoppingListId));

  const [errorMessage, setErrorMessage] = useState(null);
  // const [warningMessage, setWarningMessage] = useState(null);
  const [creationInProgress, setCreationInProgress] = useState(false);
  const [newListPageVisible, setNewListPageVisible] = useState(false);

  const [lazyListProductsBoard] = useLazyListProductsBoardQuery();

  const [createProductsBoard] = useCreateProductsBoardMutation();

  useEffect(() => {
    if (currentSpaceMembership && !isProductsBoardLoading && !productsBoard) {
      ensureDefaultProductsBoardExists(
        currentSpaceMembership.id,
        lazyListProductsBoard,
        createProductsBoard,
      );
    }
  }, [
    createProductsBoard,
    currentSpaceMembership,
    isProductsBoardLoading,
    lazyListProductsBoard,
    productsBoard,
  ]);

  // const mealsInBasket = mealIDsInBasket.map(
  //   (mealID) => allMeals[mealID] || allSharedMeals[mealID],
  // );

  // const { errors: validationWarnings } =
  //   menuInputForShoppingListWithValidationErrors(
  //     mealsInBasket,
  //     DEFAULT_SHOPPING_LIST_SERVINGS,
  //   );
  // const warningsGroupedByMealID = groupBy(validationWarnings, 'mealID', 'none');

  // const validationWarningText = `We have hit an internal error with some of the recipes. These ingredients will not be added to the shopping list: ${Object.keys(
  //   warningsGroupedByMealID,
  // )
  //   .map((mealID) => {
  //     const joinedMessage = warningsGroupedByMealID[mealID]
  //       .map((err) => err.message)
  //       .join('.\n');
  //     if (mealID === 'none') {
  //       return joinedMessage;
  //     }
  //     // eslint-disable-next-line react/prop-types
  //     return `Meal "${
  //       (allMeals[mealID] || allSharedMeals[mealID]).recipes[0].title
  //     }":\n${joinedMessage}`;
  //   })
  //   .join('.\n')}`;

  // const hasValidationWarnings = validationWarnings.length > 0;

  const resetErrorMessage = useCallback(
    () => {
      // if (hasValidationWarnings) {
      //   setWarningMessage(validationWarningText);
      // } else {
      //   setWarningMessage(null);
      // }
      // setErrorMessage(null);
    },
    [
      /* hasValidationWarnings, validationWarningText */
    ],
  );

  const onCreateNewShoppingList = async (title) => {
    setCreationInProgress(true);
    try {
      const shoppingListWithoutId = {
        title,
        items: [],
        meals: [],
      };
      const input = newShoppingListWithDefaultFields(
        productsBoardID,
        shoppingListWithoutId,
        currentSpaceMembership?.id,
      );
      const { data: shoppingList } = await createShoppingList(input);
      tracker(userProfile, [], ['numShoppingListsCreated']);
      const shoppingListGroup = productsBoard.shoppingListGroups[0];
      const position = 0;
      /* To add at the bottom: shoppingListGroup.shoppingListIDs ? shoppingListGroup.shoppingListIDs.length : 0 */
      const newShoppingListGroup = shoppingListGroupWithShoppingListAdded(
        shoppingListGroup,
        shoppingList.id,
        position,
      );
      const newProductsBoard = productsBoardWithShoppingListGroupsReplaced(
        productsBoard,
        [newShoppingListGroup],
      );
      updateProductsBoard(newProductsBoard);
      dispatch(mealBasketSelectedShoppingListIDChanged(shoppingList.id));
      setNewListPageVisible(false);
    } finally {
      setCreationInProgress(false);
    }
  };

  const onGoToNewShoppingListTitle = () => {
    setNewListPageVisible(true);
  };

  useEffect(() => {
    resetErrorMessage();
  }, [resetErrorMessage]);

  const onDismiss = () => {
    resetErrorMessage();
    setNewListPageVisible(false);
    onClosePopover();
  };

  const shoppingListOptions = Object.values(existingShoppingLists || {})
    .sort(
      // reverse sort by updatedAt - most recently updated list first
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .map((sl) => ({
      shoppingListId: sl.id,
      title: sl.title,
    }));

  const onClickOK = async () => {
    if (
      !selectedShoppingListID ||
      selectedShoppingListID === NEW_SHOPPING_LIST_PSEUDO_ID
    ) {
      return;
    }

    setCreationInProgress(true);

    const afterCreatingShoppingList = () => {
      setCreationInProgress(false);
      const shoppingListTitle = shoppingListOptions.find(
        (option) => option.shoppingListId === selectedShoppingListID,
      )?.title;
      console.log(`Meals added to ${shoppingListTitle}`);
      setSnackbarNotificationText(`Meals added to ${shoppingListTitle}`);
      clearMealBasket();
      onDismiss();
    };

    const shoppingList = (existingShoppingLists || {})[selectedShoppingListID];

    try {
      tracker(userProfile, ['Meals added to shopping list']);
      const fullMealsToAdd = mealBasketItems.map((item) => item.meal);
      // console.log('Meals to add:', mealsToAdd);
      const compiledShoppingList = await compileShoppingListOperation(
        fullMealsToAdd,
        DEFAULT_SHOPPING_LIST_SERVINGS,
        shoppingList.items,
        locale,
      );
      console.log(compiledShoppingList);
      const updatedShoppingList = {
        ...shoppingList,
        items: compiledShoppingList.items,
        meals: shoppingList.meals.concat(compiledShoppingList.meals),
      };
      await updateShoppingList(updatedShoppingList);
      afterCreatingShoppingList();
    } catch (e) {
      setErrorMessage(
        "Sorry we've encountered an internal error, please try again after some time",
      );
      throw e;
    }
  };

  const newListCreationMandatory = shoppingListOptions.length === 0;

  if (shoppingListsLoading) {
    return <MealBasketPageLoading />;
  }

  return newListPageVisible || newListCreationMandatory ? (
    <MealBasketPageNewList
      onCreateNewShoppingList={onCreateNewShoppingList}
      backButtonDisabled={newListCreationMandatory}
      creationInProgress={creationInProgress}
      isProductsBoardLoading={isProductsBoardLoading}
      onBack={() => setNewListPageVisible(false)}
      onDismiss={onDismiss}
    />
  ) : (
    <MealBasketPageListMeals
      mealBasketItems={mealBasketItems}
      shoppingListOptions={shoppingListOptions}
      warningMessage={null}
      errorMessage={errorMessage}
      productsBoardID={productsBoardID}
      selectedShoppingListID={selectedShoppingListID}
      onChangeShoppingList={selectedShoppingListIDChanged}
      creationInProgress={creationInProgress}
      onClickOK={onClickOK}
      onDismiss={onDismiss}
      mealRemovedFromMealBasket={mealRemovedFromMealBasket}
      onGoToNewShoppingListTitle={onGoToNewShoppingListTitle}
    />
  );
};

MealBasketContainer.propTypes = {
  onClosePopover: PropTypes.func.isRequired,
  setSnackbarNotificationText: PropTypes.func.isRequired,
};

export default MealBasketContainer;
